import { MutationConfirmation } from '~/components/AIInsightsSidebar/MutationConfirmation/mutationConfirmation.types';
import {
  A2uiSurfacePayload,
  buildOperationsEnvelope,
  mergeSubmittedValues,
} from '~/components/AIInsightsUI/a2ui/operationsEnvelope';
import { Block } from '~/components/AIInsightsUI/blockTypes';
import {
  ConversationAttachment,
  ConversationMessage,
  ConversationRole,
  MessageCitation as GqlMessageCitation,
  MessageSource as GqlMessageSource,
} from '~/types/api.graphql';

import { extractCitationText } from '../AIInsightsSidebar/WebSearch/citationUtils';
import { MessageCitation, MessageSource, ThinkingSource } from '../AIInsightsSidebar/WebSearch/webSearch.types';

export type { MessageCitation, MessageSource, ThinkingSource };

// A standard A2UI v0.9 operation (createSurface / updateDataModel / updateComponents). Kept
// opaque here — the @copilotkit/a2ui-renderer stack validates and applies it.
export type A2uiOperation = Record<string, unknown>;

// The live submit event the A2UI renderer hands us: `context` is the surface's data model. The
// sidebar maps it onto the agent's a2uiSubmission transport (formId = surfaceId, values = context).
export interface A2uiUserAction {
  name: string;
  surfaceId: string;
  sourceComponentId?: string;
  context?: Record<string, unknown>;
}

// Present on a persisted user turn that was an A2UI form submission (a hidden node, body null).
// Carries only display-safe fields; `displayValues` (never the raw values) rehydrates the surface.
export interface A2uiSubmission {
  formId: string;
  status: string;
  displayValues?: Record<string, unknown>;
  submittedAt?: string | null;
}

export type PdfReport = {
  downloadUrl?: string;
  expiresAt?: string;
  relativePath: string;
};

export type MessagePart =
  | { type: 'text'; text: string }
  | { type: 'block'; block: Block }
  | { type: 'a2ui'; operations: A2uiOperation[]; surfaceId: string }
  | { type: 'confirmation'; confirmation: MutationConfirmation }
  | { type: 'pdfReport'; report: PdfReport };

export type UIMessage = ConversationMessage & {
  parts?: MessagePart[];
  citations?: MessageCitation[];
  sources?: MessageSource[];
  // Present on persisted user turns that were A2UI form submissions (body is null for these);
  // consumed by hydrateA2uiHistory to hide the bubble and hydrate the submitted surface.
  a2uiSubmission?: A2uiSubmission;
};

// =============================================================================
// Enums
// =============================================================================

export enum ConversationEntityType {
  Audience = 'audience',
}

// =============================================================================
// Constants
// =============================================================================

export const ERROR_MESSAGE_PREFIX = 'error-';
export const MESSAGES_PER_PAGE = 20;
export const SCROLL_THRESHOLD = 100;
export const USAGE_QUERY_DELAY = 10_000;

// =============================================================================
// String helpers
// =============================================================================

// Remove leading newlines to avoid initial padding in messages
export const stripLeadingNewlines = (text: string): string => text.replace(/^\n+/, '');

// =============================================================================
// Date helpers
// =============================================================================

export const formatMessageTimestamp = (messageId: string): string | null => {
  // Extract timestamp from message IDs created locally (format: prefix-timestamp)
  const parts = messageId.split('-');
  const timestamp = parseInt(parts[parts.length - 1], 10);

  if (isNaN(timestamp) || timestamp < 1000000000000) return null;

  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// =============================================================================
// Message helpers
// =============================================================================

export const removeLastErrorMessage = (messages: UIMessage[]): UIMessage[] => {
  const lastMessage = messages[messages.length - 1];
  const isError = lastMessage?.id.startsWith(ERROR_MESSAGE_PREFIX) && lastMessage?.role === ConversationRole.Assistant;
  return isError ? messages.slice(0, -1) : messages;
};

export type ApiMessageWithSources = ConversationMessage & {
  citations?: GqlMessageCitation[] | null;
  sources?: GqlMessageSource[] | null;
};

export const mapApiMessageToUIMessage = ({
  block,
  a2ui,
  mutationConfirmation,
  citations,
  sources,
  pdfReports,
  ...message
}: ApiMessageWithSources): UIMessage => {
  const parts: MessagePart[] = [];
  if (message.body) {
    parts.push({ type: 'text', text: message.body });
  }
  if (block) {
    parts.push({ type: 'block', block: block as Block });
  }
  const surface = a2ui as A2uiSurfacePayload | null | undefined;
  if (surface?.components && surface.surfaceId) {
    parts.push({
      type: 'a2ui',
      operations: buildOperationsEnvelope(surface),
      surfaceId: surface.surfaceId,
    });
  }
  const confirmation = mutationConfirmation as MutationConfirmation | null | undefined;
  if (confirmation?.interruptId) {
    parts.push({ type: 'confirmation', confirmation });
  }
  (pdfReports ?? []).forEach((report) =>
    parts.push({ type: 'pdfReport', report: { relativePath: report.relativePath } }),
  );
  return {
    ...message,
    parts: parts.length > 0 ? parts : undefined,
    citations: (citations ?? []).map(
      (c, i): MessageCitation => ({
        title: c.title,
        url: c.url ?? undefined,
        text: extractCitationText(c.text),
        start: c.start ?? 0,
        end: c.end ?? 0,
        sourceNumber: i + 1,
      }),
    ),
    sources: (sources ?? []).map((s): MessageSource => ({ title: s.title, url: s.url })),
  };
};

// Post-processes a reloaded conversation (CHRONOLOGICAL order — oldest first) so A2UI
// submissions replay like the live session:
// - submission user turns (a2uiSubmission present) are dropped from the output (submitting never
//   creates a bubble live);
// - each submission pairs with the NEAREST PRECEDING UNCONSUMED surface with the same formId
//   (formId is unique per rendered surface), marking it used (disabled) and merging the submitted
//   displayValues into its data model;
// - an orphan submission (its surface lives in a not-yet-fetched older page) still hides its
//   bubble; the caller re-runs this over the merged list once more pages load, resolving it.
// Pure: clones messages/parts instead of mutating (inputs derive from the Apollo cache).
export const hydrateA2uiHistory = (
  chronologicalMessages: UIMessage[],
): { messages: UIMessage[]; usedSurfaceKeys: Set<string> } => {
  const usedSurfaceKeys = new Set<string>();
  const openSurfaces: { messageIndex: number; partIndex: number; surfaceId: string }[] = [];
  const messages = [...chronologicalMessages];

  for (let messageIndex = 0; messageIndex < messages.length; messageIndex++) {
    const message = messages[messageIndex];
    const submission = message.a2uiSubmission as A2uiSubmission | null | undefined;

    if (message.role === ConversationRole.User && submission) {
      const { formId } = submission;
      for (let openIndex = openSurfaces.length - 1; openIndex >= 0; openIndex--) {
        const open = openSurfaces[openIndex];
        if (open.surfaceId !== formId) continue;
        openSurfaces.splice(openIndex, 1);
        const surfaceMessage = messages[open.messageIndex];
        usedSurfaceKeys.add(`${surfaceMessage.id}:${open.partIndex}`);
        const parts = [...(surfaceMessage.parts ?? [])];
        const part = parts[open.partIndex];
        if (part?.type === 'a2ui') {
          parts[open.partIndex] = {
            ...part,
            operations: mergeSubmittedValues(part.operations, formId, submission.displayValues ?? {}),
          };
          messages[open.messageIndex] = { ...surfaceMessage, parts };
        }
        break;
      }
      continue;
    }

    message.parts?.forEach((part, partIndex) => {
      if (part.type === 'a2ui') {
        openSurfaces.push({ messageIndex, partIndex, surfaceId: part.surfaceId });
      }
    });
  }

  return {
    messages: messages.filter((message) => !message.a2uiSubmission),
    usedSurfaceKeys,
  };
};

export const createUserMessage = (body: string, attachments?: ConversationAttachment[]): UIMessage =>
  ({ id: `user-${Date.now()}`, body, role: ConversationRole.User, attachments } as UIMessage);

// `parts` drives the sidebar's ordered text/chart rendering. `body` is kept as the joined text of
// all text parts so consumers that only render `message.body` (e.g. AIInsightsModal) keep working.
export const createAssistantMessage = (
  parts: MessagePart[],
  citations: MessageCitation[] = [],
  sources: MessageSource[] = [],
): UIMessage =>
  ({
    id: `assistant-${Date.now()}`,
    role: ConversationRole.Assistant,
    parts,
    citations,
    sources,
    body: parts
      .filter((part): part is Extract<MessagePart, { type: 'text' }> => part.type === 'text')
      .map((part) => part.text)
      .join('\n'),
  } as UIMessage);

export const createErrorMessage = (body: string): UIMessage =>
  ({ id: `${ERROR_MESSAGE_PREFIX}${Date.now()}`, body, role: ConversationRole.Assistant } as UIMessage);

// =============================================================================
// Deeplink helpers
// =============================================================================

// Converts the current browser path to an amp:// deeplink.
// If an entity deeplink is provided (e.g. amp://audiences/71?name=Acme), any sub-path beyond
// the entity base (e.g. /lookalikes) is inserted before the query string.
// For non-entity pages, strips /app/ and prefixes with amp:// (e.g. /app/dashboard → amp://dashboard).
export const computeCurrentLocationDeeplink = (entityDeepLink: string | null, pathname: string): string => {
  const ampProtocol = 'amp://';
  if (!entityDeepLink || !entityDeepLink.startsWith(ampProtocol)) {
    const withoutApp = pathname.replace(/^\/app(\/|$)/, '');
    return `${ampProtocol}${withoutApp}`;
  }

  const withoutProtocol = entityDeepLink.slice(ampProtocol.length);
  const queryStart = withoutProtocol.indexOf('?');
  const pathPart = queryStart >= 0 ? withoutProtocol.slice(0, queryStart) : withoutProtocol;
  const queryString = queryStart >= 0 ? withoutProtocol.slice(queryStart + 1) : '';
  const entityBasePath = `/app/${pathPart}`;

  if (pathname !== entityBasePath && !pathname.startsWith(`${entityBasePath}/`)) {
    const withoutApp = pathname.replace(/^\/app(\/|$)/, '');
    return `${ampProtocol}${withoutApp}`;
  }

  const extraPath = pathname.slice(entityBasePath.length);
  const newPath = `${ampProtocol}${pathPart}${extraPath}`;
  return queryString ? `${newPath}?${queryString}` : newPath;
};
